import rison from 'rison';

import {IDatabase, IDatabaseForCreation, ApiParams, ISort, DATABASE_LOADING_SOURCE} from '../models';
import {api} from './Api';
import {
  buildConnectionUri,
  isBigQuery,
  isClickhouse,
  isGoogleSheets,
  isRockset,
  isSnowflake,
  isVertica,
  parseSqlUri,
  wrapEncryptedExtra,
  isNullUndefEmptyStr,
  isMongoBi,
  isAmazonAthena,
  isOracle,
  isDatabricks,
} from '../utils/databases';
import {getRequestParams} from "../utils/api";

export const getDatabases = (
  params: ApiParams | undefined,
  sort: ISort | undefined,
  source?: DATABASE_LOADING_SOURCE
) => {
  /*
    * Get all databases
    * If params and sort object values are available then get all database with sharing settings details
    * If params and sort all object values are undefined get all databases without sharing settings
    * Above two cases are introduced to solve the performance issue of get all databases with sharing settings details
   */
  const requestParams = getRequestParams(params, sort);
  const _source = source || '';

  return (!isNullUndefEmptyStr(params) || !isNullUndefEmptyStr(sort)) ?
    api.get(`/database/?q=${rison.encode(requestParams)}&source=${_source}`).then(res => res.data) :
    api.get(`/database/?action=sql_block&source=${_source}`).then(res => res.data);
};

export const getMostRecentDatabase = () => {
  return api.get('/database/?action=most_recent_database').then(res => res.data);
};

export const getDatabasesMetadata = () => {
  // @TODO - rename this as this is not metadata?
  return api.get(`/database/_info`).then(res => res.data);
}

const extractFromUri = (connectionString: string, regex: RegExp): string | undefined => {
  const matches = connectionString.match(regex);
  return matches && matches[1] ? matches[1] : undefined;
};

const extractQueryParamFromUri = (connectionString: string, paramName: string): string | undefined => {
  const regex = new RegExp(`${paramName}=([^&]+)`);
  const value = extractFromUri(connectionString, regex);
  return value ? decodeURIComponent(value) : undefined;
};

// Helper Function
const parseDatabaseData = (data: any) => {
  const result: IDatabase = {...data.result};
  /*
   * parsed data is useful when editing a Database connection (maybe move this to reducer?)
  */

  const connectionString = result.sqlalchemy_uri;

  if (isSnowflake(result.backend)) {
    try {
      const account = result.host || ''; /* account + region */
      const [database, schema] = (result.database || '').split('/');
      const parsedUrl: any = parseSqlUri(result.sqlalchemy_uri, true);

      result.account = account;
      result.database = database;
      result.schema = schema;

      if (parsedUrl.query && parsedUrl.query.role) {
        result.role = parsedUrl.query.role;
      }
      if (parsedUrl.query && parsedUrl.query.warehouse) {
        result.warehouse = parsedUrl.query.warehouse;
      }
    } catch (e) {
      // continue
    }
  } else if (isClickhouse(result.backend)) {
    try {
      const parsedUrl: any = parseSqlUri(result.sqlalchemy_uri, true);

      if (parsedUrl.query && parsedUrl.query.protocol) {
        result.protocol = parsedUrl.query.protocol;
      }
    } catch (e) {
      // continue
    }
  } else if (isRockset(result.backend)) {
    try {
      result.region = result.host;
    } catch (e) {
      // continue
    }
  } else if (isGoogleSheets(result.backend)) {
    try {
      if (result.allow_dml) {
        result.type = 'private';
      }
      const catalog: object | undefined = JSON.parse(result.extra).engine_params?.catalog;
      if (catalog) {
        const sheetArr = [];
        for (const [key, value] of Object.entries(catalog)) {
          sheetArr.push({name: key, value: value});
        }
        result.sheets = sheetArr;
      }
    } catch (e) {
      // continue
    }
  } else if (isAmazonAthena(result.backend)) {
    try {
      result.aws_access_key_id = result.username;
      result.schema = result.database;
      if (connectionString) {
        result.endpoint = extractFromUri(connectionString, /@(.*):/);
        result.s3_staging_directory = extractQueryParamFromUri(connectionString, "s3_staging_dir");
        result.workgroup = extractQueryParamFromUri(connectionString, "work_group");
      }
    } catch (e) {
      // continue
    }
  } else if (isDatabricks(result.backend)) {
    try {
      // Retrieve the http_path from the connection string
      const parsedUrl: any = parseSqlUri(result.sqlalchemy_uri, true);
      result.http_path = parsedUrl.query && parsedUrl.query.http_path;
    } catch (e) {
      // continue
    }
  }

  return {
    ...data,
    result: result
  };
}

export const getDatabase = (uid: string, page: number, page_size: number, withSchemas?: boolean) => {
  return withSchemas ?
    Promise.all([
      api.get(`/database/${uid}`).then(res => parseDatabaseData(res.data)),
      api.get(`/es/schema/?q=(filters:!((col:database_uid,opr:eq,value:'${uid}')),page:${page},page_size:${page_size})`)
        .then(res => res.data),
    ]).then(([database, schemas]) => ({
      ...database,
      result: {
        ...database.result,
        schemas: schemas.result
      }
    })) :
    api.get(`/database/${uid}`).then(res => parseDatabaseData(res.data));
};

export const getDatabaseMetadata = (uid: string | string[]) => {
  /*
   * Get database, schema, table, column info from ES
   */
  const getAllData = (uid: string) => {
    return Promise.all([
      api.get(`/es/database/${uid}/metadata`).then((res: any) => res.data),
    ]).then(([result]) => ({
      [uid]: result
    }));
  }

  return Promise.all(
    typeof uid === 'string' ? [getAllData(uid)] : uid.map(getAllData)
  );
};

export const getDatabaseSharingSettings = (uid: string) => {
  return api.get(`/database/${uid}/sharing_settings`).then((res: any) => res.data);
};

export const getSelectedDatabaseSharingSettings = (uidArr: string[]) => {

  return Promise.all(
    uidArr
      .map((uid: string) =>
        getDatabaseSharingSettings(uid)
          .then((res: any) => ({...(res?.result || {}), uid}))
      )
  );
};

export const getCurrentUserPermissions = (uid: string, userId: string) => {
  return api.get(`/database/${uid}/current_user_permissions`).then((res: any) => res.data);
}

const DEFAULT_CREATE_DATABASE_EXTRA = {
  metadata_params: {},
  engine_params: {
    isolation_level: 'AUTOCOMMIT'  // useful if allow_dml is True, which in our case it is
  }
};

const DEFAULT_CREATE_DATABASE = {
  allow_csv_upload: true, // not used, but let's default it to true
  allow_ctas: true, // allow create table AS
  allow_cvas: true, // allow create view AS
  allow_dml: true, // allow users to run non-SELECT statements in SQL Lab
  allow_multi_schema_metadata_fetch: true, // let's leave it to true
  allow_run_async: true,
  cache_timeout: 0,
  encrypted_extra: null, // used for BigQuery
  expose_in_sqllab: true,
  extra: JSON.stringify(DEFAULT_CREATE_DATABASE_EXTRA, null, 2),
  force_ctas_schema: '',
  impersonate_user: false,
  server_cert: null,  // not used yet, this requires another input in case it will be used
}

export const createDatabase = (databaseData: IDatabaseForCreation) => {
  const {
    engine,
    username,
    password,
    host,
    port,
    database_name: database,
    encrypted_extra,
    account,
    schema,
    warehouse,
    role,
    protocol,
    region,
    api_key,
    sheets,
    aws_access_key_id,
    aws_secret_access_key,
    endpoint,
    s3_staging_directory,
    workgroup,
    private_key,
    private_key_passphrase,
    token,
    http_path,
  } = databaseData;

  let _DEFAULT_CREATE_DATABASE = JSON.parse(JSON.stringify(DEFAULT_CREATE_DATABASE));

  if (isBigQuery(engine) || isSnowflake(engine) || isClickhouse(engine) || isRockset(engine) || isVertica(engine) || isGoogleSheets(engine)) {
    _DEFAULT_CREATE_DATABASE.extra = JSON.stringify(
      {
        ...DEFAULT_CREATE_DATABASE_EXTRA,
        engine_params: sheets ? {
          catalog: Object.fromEntries(new Map(sheets.map(sheet => {
            return [sheet.name, sheet.value]
          })))
        } : {}
      }, null, 2)
  }

  if (isMongoBi(engine)) {
    _DEFAULT_CREATE_DATABASE.extra = JSON.stringify(
      {
        ...DEFAULT_CREATE_DATABASE_EXTRA,
        engine_params: {
          connect_args: {
            ssl: {
              mode: "PREFERRED"
            },
          }
        }
      }, null, 2)
  }

  if (isAmazonAthena(engine) || isOracle(engine) || isDatabricks(engine)) {
    _DEFAULT_CREATE_DATABASE.extra = JSON.stringify(
      {
        ...DEFAULT_CREATE_DATABASE_EXTRA,
        engine_params: {}
      }, null, 2)
  }

  let encrypted_info: string | null = DEFAULT_CREATE_DATABASE.encrypted_extra;
  if (isBigQuery(engine)) {
    encrypted_info = wrapEncryptedExtra(encrypted_extra, 'credentials_info');
  } else if (isGoogleSheets(engine)) {
    // for public google sheets we don't add service_account_info to engine
    if (encrypted_extra) {
      encrypted_info = wrapEncryptedExtra(encrypted_extra, 'service_account_info');
    } else {
      _DEFAULT_CREATE_DATABASE.allow_dml = false;
    }
  } else if (isSnowflake(engine) && private_key && private_key_passphrase) {
    encrypted_info = JSON.stringify({
      auth_method: "keypair",
      auth_params: {
        privatekey_body: private_key.replace(/\n/g, '\\n'),
        privatekey_pass: private_key_passphrase,
      },
    });
  }

  return api.post(`/database/`, {
    ..._DEFAULT_CREATE_DATABASE,
    encrypted_extra: encrypted_info,
    database_name: databaseData.name,
    sqlalchemy_uri: buildConnectionUri(
      engine,
      host,
      port,
      username,
      password,
      database,
      account,
      schema,
      warehouse,
      role,
      protocol,
      region,
      api_key,
      aws_access_key_id,
      aws_secret_access_key,
      endpoint,
      s3_staging_directory,
      workgroup,
      token,
      http_path,
    )
  }).then(res => res.data);
};

export const editDatabase = (uid: string, databaseData: IDatabaseForCreation) => {
  const {
    engine,
    username,
    password,
    host,
    port,
    database_name: database,
    encrypted_extra,
    account,
    schema,
    warehouse,
    role,
    protocol,
    region,
    api_key,
    sheets,
    aws_access_key_id,
    aws_secret_access_key,
    endpoint,
    s3_staging_directory,
    workgroup,
    private_key,
    private_key_passphrase,
    token,
    http_path,
  } = databaseData;

  let _DEFAULT_CREATE_DATABASE = JSON.parse(JSON.stringify(DEFAULT_CREATE_DATABASE));

  if (isBigQuery(engine) || isSnowflake(engine) || isClickhouse(engine) || isRockset(engine) || isVertica(engine) || isGoogleSheets(engine)) {
    _DEFAULT_CREATE_DATABASE.extra = JSON.stringify(
      {
        ...DEFAULT_CREATE_DATABASE_EXTRA,
        engine_params: sheets ? {
          catalog: Object.fromEntries(new Map(sheets.map(sheet => {
            return [sheet.name, sheet.value]
          })))
        } : {}
      }, null, 2)
  }

  if (isMongoBi(engine)) {
    _DEFAULT_CREATE_DATABASE.extra = JSON.stringify(
      {
        ...DEFAULT_CREATE_DATABASE_EXTRA,
        engine_params: {
          connect_args: {
            ssl: {
              mode: "PREFERRED"
            },
          }
        }
      }, null, 2)
  }

  if (isAmazonAthena(engine) || isOracle(engine) || isDatabricks(engine)) {
    _DEFAULT_CREATE_DATABASE.extra = JSON.stringify(
      {
        ...DEFAULT_CREATE_DATABASE_EXTRA,
        engine_params: {}
      }, null, 2)
  }

  let encrypted_info: string | null = DEFAULT_CREATE_DATABASE.encrypted_extra;
  let allow_dml: boolean | null = DEFAULT_CREATE_DATABASE.allow_dml;
  if (isBigQuery(engine)) {
    encrypted_info = wrapEncryptedExtra(encrypted_extra, 'credentials_info');
  } else if (isGoogleSheets(engine)) {
    // for public google sheets we don't add service_account_info to engine
    if (encrypted_extra) {
      encrypted_info = wrapEncryptedExtra(encrypted_extra, 'service_account_info');
    } else {
      allow_dml = false;
    }
  } else if (isSnowflake(engine) && private_key && private_key_passphrase) {
    encrypted_info = JSON.stringify({
      auth_method: "keypair",
      auth_params: {
        privatekey_body: private_key.replace(/\n/g, '\\n'),
        privatekey_pass: private_key_passphrase,
      },
    });
  }

  return api.put(`/database/${uid}`, {
    ..._DEFAULT_CREATE_DATABASE,
    database_name: databaseData.name,
    encrypted_extra: encrypted_info,
    sqlalchemy_uri: buildConnectionUri(
      engine, host,
      port,
      username, password,
      database,
      account,
      schema,
      warehouse, role,
      protocol,
      region,
      api_key,
      aws_access_key_id,
      aws_secret_access_key,
      endpoint,
      s3_staging_directory,
      workgroup,
      token,
      http_path,
    ),
    allow_dml
  }).then(res => res.data);
};

export const removeDatabase = (uid: string) => {
  return api.delete(`/database/${uid}`).then(res => res.data);
};

export const shareDatabaseWithUser = (databaseUid: string, userUid: string, permission: string) => {
  const payload = {
    user_uid: userUid,
    permission
  };

  return api.post(`/database/${databaseUid}/share_with_user`, payload).then(res => res.data);
};

export const shareDatabaseWithGroup = (databaseUid: string, groupUid: string, permission: string) => {
  const payload = {
    group_uid: groupUid,
    permission
  };

  return api.post(`/database/${databaseUid}/share_with_group`, payload).then(res => res.data);
};

export const shareDatabaseWithWorkspace = (databaseUid: string, permission: string) => {
  const payload = {
    permission
  };

  return api.post(`/database/${databaseUid}/share_with_workspace`, payload).then(res => res.data);
};
